import "./AdsCreative.scss";
import AdsCreativePreviewCard from "./components/AdsCreativePreviewCard/AdsCreativePreviewCard";
import { AddCircleOutlined } from "@mui/icons-material";
import AdsCreativeHotelList from "./components/AdsCreativeHotelList/AdsCreativeHotelList";
import { useState, useEffect, useMemo, useRef, useCallback } from "react";
import { computeHotelStatus, STATUS_LABELS } from "./utils/hotelCreativeStatus";
import useMetaHotels from "../../Utils/useMetaHotels";
import { useSelector } from "react-redux";

const defaultCards = () => [{ ad_card_id: 1 }];

const hasRealCardData = (cards) =>
  Array.isArray(cards) &&
  cards.some(
    (card) =>
      card?.headline?.assetContent ||
      card?.primary_text?.assetContent ||
      card?.web_site_url?.url
  );

const groupCardsByHotel = (cards, hotels) => {
  const grouped = {};
  hotels.forEach((h) => { grouped[String(h.hotel_id)] = []; });
  let globalId = 1;
  cards.forEach((card) => {
    const cardHotelId = String(card?.hotel_id || hotels[0]?.hotel_id);
    if (!grouped[cardHotelId]) grouped[cardHotelId] = [];
    grouped[cardHotelId].push({ ...card, ad_card_id: globalId++ });
  });
  const result = {};
  hotels.forEach((h) => {
    result[h.hotel_id] = grouped[String(h.hotel_id)] || [];
  });
  return result;
};

const AdsCreative = ({ hotelId, adsCreativeCards = [{ ad_card_id: 1 }], setAdsCreativeCards, selectedHotels = [], onAllDoneChange, isMultiHotelEnabled = false, selectedLanguage = [] }) => {
  const accountId = useSelector((state) => state?.account?.account?.account_id);
  const { hotelOptions } = useMetaHotels(accountId);
  const [hotelList, setHotelList] = useState([]);
  const [activeHotelId, setActiveHotelId] = useState(null);
  const [searchQuery, setSearchQuery] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [hotelCards, setHotelCards] = useState({});

  // Track what we last pushed to parent so we can ignore it bouncing back as a prop
  const lastSyncedRef = useRef(null);
  const initialSeedDoneRef = useRef(false);

  // Filter hotels by selectedHotels
  useEffect(() => {
    let hotels = hotelOptions;
    if (selectedHotels && selectedHotels.length > 0) {
      const selectedSet = selectedHotels.map(String);
      hotels = hotels.filter((h) => selectedSet.includes(String(h.hotel_id)));
    }
    setHotelList(hotels);
    setActiveHotelId((prev) => prev || hotels[0]?.hotel_id || null);
  }, [hotelOptions, selectedHotels]);

  // Seed hotelCards from parent adsCreativeCards
  useEffect(() => {
    if (!hotelList.length) return;
    // If this is the same array we just synced to parent, skip
    if (lastSyncedRef.current === adsCreativeCards) return;

    const realData = hasRealCardData(adsCreativeCards);
    if (realData) {
      setHotelCards(groupCardsByHotel(adsCreativeCards, hotelList));
      initialSeedDoneRef.current = true;
    } else if (!initialSeedDoneRef.current) {
      setHotelCards({ [hotelList[0].hotel_id]: defaultCards() });
      initialSeedDoneRef.current = true;
    }
  }, [adsCreativeCards, hotelList]);

  // Sync hotelCards to parent
  useEffect(() => {
    if (Object.keys(hotelCards).length === 0) return;
    const allCards = [];
    Object.entries(hotelCards).forEach(([hId, cards]) => {
      if (!hId || hId === "null" || hId === "undefined") return;
      cards.forEach((card) => { allCards.push({ ...card, hotel_id: hId }); });
    });
    if (allCards.length > 0) {
      lastSyncedRef.current = allCards;
      setAdsCreativeCards(allCards);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hotelCards]);

  const activeCards = hotelCards[activeHotelId] || defaultCards();

  const setActiveCards = useCallback((updater) => {
    setHotelCards((prev) => {
      const current = prev[activeHotelId] || defaultCards();
      const next = typeof updater === "function" ? updater(current) : updater;
      return { ...prev, [activeHotelId]: next };
    });
  }, [activeHotelId]);

  const hotelStatuses = useMemo(() => {
    const statuses = {};
    hotelList.forEach((hotel) => {
      statuses[hotel.hotel_id] = computeHotelStatus(hotelCards[hotel.hotel_id]);
    });
    return statuses;
  }, [hotelList, hotelCards]);

  const hotelsWithStatus = useMemo(
    () => hotelList.map((hotel) => ({ ...hotel, status: hotelStatuses[hotel.hotel_id] || "not_started" })),
    [hotelList, hotelStatuses]
  );

  // Notify parent when all hotels are done (or not)
  const allDone = useMemo(() => {
    if (!isMultiHotelEnabled) {
      // Single hotel mode: check if active cards are all complete
      const cards = hotelCards[activeHotelId] || [];
      if (cards.length === 0) return false;
      return computeHotelStatus(cards) === "done";
    }
    return hotelList.length > 0 && hotelList.every((hotel) => hotelStatuses[hotel.hotel_id] === "done");
  }, [isMultiHotelEnabled, hotelList, hotelStatuses, hotelCards, activeHotelId]);

  useEffect(() => {
    if (onAllDoneChange) onAllDoneChange(allDone);
  }, [allDone, onAllDoneChange]);

  const activeHotel = hotelsWithStatus.find((h) => h.hotel_id === activeHotelId);
  const filteredHotels = hotelsWithStatus.filter((hotel) => {
    const matchesSearch = !searchQuery || hotel.hotel_name?.toLowerCase().includes(searchQuery.toLowerCase()) || (hotel.location && hotel.location.toLowerCase().includes(searchQuery.toLowerCase()));
    const matchesStatus = statusFilter === "all" || hotel.status === statusFilter;
    return matchesSearch && matchesStatus;
  });

  const doneCount = hotelsWithStatus.filter((h) => h.status === "done").length;
  const totalCount = hotelsWithStatus.length;
  const activeIndex = filteredHotels.findIndex((h) => h.hotel_id === activeHotelId);

  const handlePrev = () => { if (activeIndex > 0) setActiveHotelId(filteredHotels[activeIndex - 1].hotel_id); };
  const handleNext = () => { if (activeIndex > -1 && activeIndex < filteredHotels.length - 1) setActiveHotelId(filteredHotels[activeIndex + 1].hotel_id); };

  return (
    <div className="ads-creative-wrapper">
      {isMultiHotelEnabled && (
        <AdsCreativeHotelList
          hotels={filteredHotels}
          activeHotelId={activeHotelId}
          onSelectHotel={setActiveHotelId}
          searchQuery={searchQuery}
          setSearchQuery={setSearchQuery}
          statusFilter={statusFilter}
          setStatusFilter={setStatusFilter}
          doneCount={doneCount}
          totalCount={totalCount}
        />
      )}
      <div className="ads-creative-main-content">
        {isMultiHotelEnabled && activeHotel && (
          <div className="ads-creative-hotel-header">
            <div className="hotel-info">
              <span className="hotel-name">{activeHotel.hotel_name}</span>
              {activeHotel.location && <span className="hotel-location">{activeHotel.location}</span>}
              <span className={`hotel-status status-${activeHotel.status}`}>{STATUS_LABELS[activeHotel.status]}</span>
            </div>
            <div className="hotel-nav-actions">
              <button className="nav-btn" onClick={handlePrev} disabled={activeIndex <= 0}>&lt; Prev</button>
              <button className="nav-btn" onClick={handleNext} disabled={activeIndex === -1 || activeIndex >= filteredHotels.length - 1}>Next &gt;</button>
              
            </div>
          </div>
        )}
        <div className="ads-creative-container">
          {activeCards.map((card) => (
            <AdsCreativePreviewCard
              key={`${activeHotelId}-${card.ad_card_id}`}
              id={card.ad_card_id}
              onDelete={() => setActiveCards(activeCards.filter((c) => c.ad_card_id !== card.ad_card_id))}
              hotelId={activeHotelId || hotelId}
              adsCreativeCards={activeCards}
              setAdsCreativeCards={setActiveCards}
              selectedLanguage={selectedLanguage}
            />
          ))}
          <div className="add-new-card" onClick={() => {
            const allCardIds = Object.values(hotelCards).flat().map((c) => Number(c?.ad_card_id) || 0);
            const nextId = allCardIds.length ? Math.max(...allCardIds) + 1 : 1;
            setActiveCards([...activeCards, { ad_card_id: nextId }]);
          }}>
            <AddCircleOutlined />
            <span>ADD LANGUAGE</span>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AdsCreative;
